// A PARTNER-SZÁMLÁZÁS KÁRTYÁJÁNAK EGYETLEN alakja — a számla (havi nézet), a terv-tétel és a szolgáltatás
// ugyanezt a szerkezetet használja, hogy a lap ne három különböző terméknek látsszon:
//
//   ┌───────────────────────────────────────────────┐
//   │ [ikon] CÍM                        ELSŐDLEGES ÖSSZEG │   ← címsor: név balra, összeg jobbra
//   │ [státusz] [hatókör] [gyakoriság] [használat] …      │   ← jelvény-sor, MINDIG ugyanebben a sorrendben
//   │ meta-sor(ok): időszak · dátumok · mennyiség          │   ← másodlagos szöveg, mindenhol ugyanaz
//   │ (children: kibontható tételek, időszak-lista, PDF…)  │
//   └───────────────────────────────────────────────┘
//
// A jelvény-sorrend (a hívó adja, de ezt kérjük): státusz → hatókör → gyakoriság → használat → becslés /
// arányosított → közös számlázás. Mobilon a címsor egymás alá törik (egy oszlop, nincs vízszintes túlcsordulás).
import type { ReactNode } from 'react';

import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

export function PortalCard({
  title,
  leading,
  amount,
  badges,
  meta,
  children,
  as: Tag = 'div',
  className,
  ...rest
}: {
  title: ReactNode;
  /** Opcionális ikon-csempe a cím előtt (szolgáltatás-kártya). */
  leading?: ReactNode;
  /** A jobbra igazított elsődleges összeg (vagy „használat szerint"). */
  amount?: ReactNode;
  badges?: ReactNode;
  /** Másodlagos sorok (időszak, dátumok, mennyiség) — mindegyik `Text variant="secondary"`. */
  meta?: ReactNode;
  children?: ReactNode;
  as?: 'div' | 'li';
  className?: string;
  'data-testid'?: string;
}): JSX.Element {
  return (
    <Tag
      className={cn('card-glass space-y-2.5 overflow-hidden rounded-xl p-4', className)}
      {...rest}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-center gap-2.5">
          {leading}
          <div className="min-w-0 break-words">{title}</div>
        </div>
        {amount !== undefined && <div className="shrink-0 sm:text-right">{amount}</div>}
      </div>
      {badges !== undefined && <div className="flex flex-wrap items-center gap-1.5">{badges}</div>}
      {meta !== undefined && <div className="space-y-0.5">{meta}</div>}
      {children}
    </Tag>
  );
}

/** A kártya címe: azonosító (számlaszám — monospace) vagy név. */
export function PortalCardTitle({
  children,
  mono = false,
}: {
  children: ReactNode;
  mono?: boolean;
}): JSX.Element {
  return (
    <Text
      variant="itemTitle"
      as="h3"
      className={cn(mono && 'font-mono tabular-nums', 'break-words')}
    >
      {children}
    </Text>
  );
}
